import React, { useEffect, useRef, useState } from 'react';
import { fetchPlacaResumo } from '../../../../api/contac/contacAnalyticsApi';
import {
  downloadRelatorioCsvDrePlacas,
  downloadRelatorioXlsxDrePlacas,
} from '../../../../api/contac/contacReportsPlacasApi';
import FullScreenLoader from '../../../../components/loading/FullScreenLoader';
import ArrowDown from '../../../../img/arrow-down.svg';
import ArrowUp from '../../../../img/arrow-up.svg';
import ContacDocumentOrange from '../../../../img/contac_document_orange.svg';
import { toast } from 'react-toastify';

import BlocoEventosDaPlaca from './BlocoEventosDaPlaca';
import './ModalPlacaDre.css';
import { removerAcentos } from '../../../../shared/texto/busca-sem-acento';

interface Detalhe { mes: string; valor: number; }
interface ClassificacaoResp { classificacao: string; total: number; detalhes: Detalhe[]; }

interface Detalhamento {
  receitaMensalidade: number;
  receitaNasajonComPlaca: number;
  receitaRateio: number;

  despesaNasajonComPlaca: number;
  despesaEasy: number;
  despesaRateio: number;
  despesaEasyExcedente: number;
  despesaRecorrente: number;
  despesaRecorrenteExcedente: number;

  totaisEntrada: number;
  totaisSaida: number;
  resultado: number;
}

interface ResultadoPlaca {
  placa: string;
  modelo: string;
  ano_modelo: number;
  categoria: string;
  associado: string;
  cpf_associado: string;
  tempo_participacao_meses: number;
  regional: string;
  consultor: string;
  cidade: string;
  situacao: string;
  resultado: number;
  /** false = a placa tem movimento financeiro mas não existe no cadastro de veículos do SGA */
  cadastroSga: boolean;
  semPlacaInformada: boolean;
  detalhamento: Detalhamento;

  entradasClassificacoes: ClassificacaoResp[];
  saidasClassificacoes: ClassificacaoResp[];
  entradasClassificacoesRateio?: ClassificacaoResp[];
  saidasClassificacoesRateio?: ClassificacaoResp[];
}

const classBySign3 = (n?: number) =>
  typeof n === 'number' && n < 0 ? 'value-negative3' : 'value-positive3';

const formatCurrency = (valor: number | null | undefined) =>
  typeof valor === 'number'
    ? valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })
    : '0,00';

const formatPercent = (n: number | null | undefined) =>
  typeof n === 'number' && Number.isFinite(n)
    ? `${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
    : '—';

const pctOf = (signedValue: number, baseAbs: number) =>
  baseAbs === 0 ? null : (signedValue / baseAbs) * 100;

const renderValorPct = (
  displayValue: React.ReactNode,
  signedForPercent: number,
  baseAbs: number,
  percentClassName?: string
) => (
  <div className="dre-value-pct">
    {displayValue}
    <span className={`dre-percent ${percentClassName ?? ''}`}>
      {formatPercent(pctOf(signedForPercent, baseAbs))}
    </span>
  </div>
);

const toNum = (v: any) => (typeof v === 'number' ? v : Number(v ?? 0)) || 0;

const norm = (v: any) => removerAcentos(String(v ?? '').trim().toUpperCase());

interface ModalPlacaDreProps {
  placa: string;
  dataInicio: string;
  dataFim: string;
  isOpen: boolean;
  onClose: () => void;
  /**
   * Exibe o bloco "Eventos no período" ao fim da DRE (contac/at-11).
   *
   * **Opt-in de propósito.** Quem abre este modal hoje é o Dashboard de Placas, e lá a pergunta é
   * "quanto esta placa deu"; no Radar a pergunta é "por que este valor", e é ali que o evento
   * responde. Ligar por padrão acrescentaria uma requisição a uma tela que não pediu por ela.
   */
  mostrarEventos?: boolean;
}

const ModalPlacaDre: React.FC<ModalPlacaDreProps> = ({ placa, dataInicio, dataFim, isOpen, onClose, mostrarEventos = false }) => {
  const [resultado, setResultado] = useState<ResultadoPlaca | null>(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [expanded, setExpanded] = useState<string[]>([]);
  const modalRef = useRef<HTMLDivElement>(null);

  const toggleExpand = (key: string) =>
    setExpanded((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const handleDownloadCsv = async () => {
    try {
      setDownloading(true);

      const placaRaw = String(placa ?? '').trim().toUpperCase();
      const placaKey = placaRaw === 'SEM PLACA DEFINIDA' || placaRaw === '' ? '' : placaRaw.replace(/[^A-Z0-9]/g, '');

      await downloadRelatorioCsvDrePlacas({
        dataInicio,
        dataFim,
        placas: placaKey ? [placaKey] : ['SEM PLACA DEFINIDA'],
      });

      toast.success('Relatório CSV gerado com sucesso!');
    } catch {
      toast.error('Erro ao gerar CSV');
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadXlsx = async () => {
    try {
      setDownloading(true);

      const placaRaw = String(placa ?? '').trim().toUpperCase();
      const placaKey = placaRaw === 'SEM PLACA DEFINIDA' || placaRaw === '' ? '' : placaRaw.replace(/[^A-Z0-9]/g, '');

      await downloadRelatorioXlsxDrePlacas({
        dataInicio,
        dataFim,
        placas: placaKey ? [placaKey] : ['SEM PLACA DEFINIDA'],
      });

      toast.success('Relatório XLSX gerado com sucesso!');
    } catch {
      toast.error('Erro ao gerar XLSX');
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    let active = true;

    const run = async () => {
      try {
        setLoading(true);

        const placaRaw = String(placa ?? '').trim().toUpperCase();
        const placaKey = placaRaw === 'SEM PLACA DEFINIDA' || placaRaw === '' ? '' : placaRaw.replace(/[^A-Z0-9]/g, '');

        const res0: any = await fetchPlacaResumo({
          placa: placaKey,
          dataInicio,
          dataFim,
        });

        if (!active) return;

        const res =
          res0?.item ??
          (Array.isArray(res0?.items) ? res0.items[0] : null) ??
          res0 ??
          null;

        const d: any = res?.detalhamento && typeof res.detalhamento === 'object' ? res.detalhamento : res;

        const mesesMap = Object.fromEntries(
          Object.entries(d ?? {}).filter(([k, v]) => /^\d{2}\/\d{4}$/.test(k) && Array.isArray(v)),
        ) as Record<string, any[]>;

        if (!Object.keys(mesesMap).length) {
          setResultado(null);
          return;
        }

        const linhas = Object.entries(mesesMap).flatMap(([mes, arr]) =>
          (arr ?? []).map((x: any) => ({ ...x, __mes: mes })),
        );

        const isDetalhe = (x: any) => norm(x?.bloco) === 'DETALHE';

        const getGrupo = (x: any) => {
          const grupo = norm(x?.grupo);
          const tipo = norm(x?.tipo);
          const classificacao = norm(x?.classificacao);
          const fonte = norm(x?.fonte);

          if (grupo) return grupo;

          if (tipo === 'ENTRADA') {
            if (classificacao.includes('RATEIO')) return 'OUTRAS RECEITAS';
            return 'RECEITA OPERACIONAL';
          }

          if (tipo === 'SAIDA') {
            if (
              classificacao.includes('EXCEDENTE') ||
              classificacao.includes('RATEIO') ||
              fonte.includes('EXCEDENTE') ||
              fonte.includes('RATEIO')
            ) return 'MARGEM DE CONTRIBUICAO';
            return 'DESPESAS OPERACIONAIS';
          }

          return '';
        };

        const eqGrupo = (x: any, alvo: string) => getGrupo(x) === norm(alvo);
        const isGrupoMargem = (x: any) => getGrupo(x).includes('MARGEM');

        const sumWhere = (pred: (x: any) => boolean) =>
          linhas.reduce((acc, x) => acc + (pred(x) ? toNum(x?.valor_total) : 0), 0);

        const sumGrupo = (grupo: string, tipo: 'ENTRADA' | 'SAIDA') =>
          sumWhere((x) => isDetalhe(x) && x?.tipo === tipo && eqGrupo(x, grupo));

        const sumMargem = () =>
          sumWhere((x) => isDetalhe(x) && x?.tipo === 'SAIDA' && isGrupoMargem(x));

        const buildClassificacoesGrupo = (
          grupoMatch: (x: any) => boolean,
          tipo: 'ENTRADA' | 'SAIDA',
        ) => {
          const acc: Record<string, { classificacao: string; total: number; porMes: Record<string, number> }> = {};
          for (const x of linhas) {
            if (!isDetalhe(x)) continue;
            if (x?.tipo !== tipo) continue;
            if (!grupoMatch(x)) continue;

            const key = String(x?.classificacao ?? '').trim() || 'Sem Classificação';
            const v = toNum(x?.valor_total);

            if (!acc[key]) acc[key] = { classificacao: key, total: 0, porMes: {} };
            acc[key].total += v;
            acc[key].porMes[x.__mes] = (acc[key].porMes[x.__mes] ?? 0) + v;
          }
          return Object.values(acc)
            .map((c) => ({
              classificacao: c.classificacao,
              total: c.total,
              porMes: Object.entries(c.porMes).map(([competencia, valor]) => ({ competencia, valor })),
            }))
            .sort((a, b) => b.total - a.total);
        };

        const receitaMensalidade = sumWhere(
          (x) =>
            isDetalhe(x) &&
            x?.tipo === 'ENTRADA' &&
            eqGrupo(x, 'RECEITA OPERACIONAL') &&
            norm(x?.fonte).includes('MV_SGA'),
        );

        const receitaNasajonComPlaca = sumWhere(
          (x) =>
            isDetalhe(x) &&
            x?.tipo === 'ENTRADA' &&
            eqGrupo(x, 'RECEITA OPERACIONAL') &&
            norm(x?.fonte).includes('MV_NASAJON'),
        );

        const receitaRateio = sumGrupo('OUTRAS RECEITAS', 'ENTRADA');

        const despesaNasajonComPlaca = sumWhere(
          (x) =>
            isDetalhe(x) &&
            x?.tipo === 'SAIDA' &&
            eqGrupo(x, 'DESPESAS OPERACIONAIS') &&
            norm(x?.fonte).includes('MV_NASAJON'),
        );

        const despesaEasy = sumWhere(
          (x) =>
            isDetalhe(x) &&
            x?.tipo === 'SAIDA' &&
            eqGrupo(x, 'DESPESAS OPERACIONAIS') &&
            norm(x?.fonte).includes('EASYASSIST'),
        );

        const despesaRecorrente = sumWhere(
          (x) =>
            isDetalhe(x) &&
            x?.tipo === 'SAIDA' &&
            eqGrupo(x, 'DESPESAS OPERACIONAIS') &&
            norm(x?.fonte).includes('MV_RECORRENTE') &&
            !norm(x?.fonte).includes('EXCEDENTE'),
        );

        const despesaEasyExcedente = sumWhere(
          (x) =>
            isDetalhe(x) &&
            x?.tipo === 'SAIDA' &&
            isGrupoMargem(x) &&
            norm(x?.fonte).includes('EASY_EXCEDENTE'),
        );

        const despesaRecorrenteExcedente = sumWhere(
          (x) =>
            isDetalhe(x) &&
            x?.tipo === 'SAIDA' &&
            isGrupoMargem(x) &&
            norm(x?.fonte).includes('RECORRENTE_EXCEDENTE'),
        );

        const despesaRateio = sumMargem();

        const receitaOperacional = sumGrupo('RECEITA OPERACIONAL', 'ENTRADA');
        const outrasReceitas = receitaRateio;

        const despesasOperacionais = sumGrupo('DESPESAS OPERACIONAIS', 'SAIDA');
        const margemContribuicao = despesaRateio;

        const totaisEntrada = toNum(d?.totaisEntrada) || (receitaOperacional + outrasReceitas);
        const totaisSaida = toNum(d?.totaisSaida) || (despesasOperacionais + margemContribuicao);
        const resultadoBack = toNum(d?.resultado) || (totaisEntrada - totaisSaida);

        const entradasOp = mapClassifs(buildClassificacoesGrupo((x) => eqGrupo(x, 'RECEITA OPERACIONAL'), 'ENTRADA'));
        const entradasRateio = mapClassifs(buildClassificacoesGrupo((x) => eqGrupo(x, 'OUTRAS RECEITAS'), 'ENTRADA'));
        const saidasOp = mapClassifs(buildClassificacoesGrupo((x) => eqGrupo(x, 'DESPESAS OPERACIONAIS'), 'SAIDA'));
        const saidasRateio = mapClassifs(buildClassificacoesGrupo((x) => isGrupoMargem(x), 'SAIDA'));

        const adapted: ResultadoPlaca = {
          placa: res?.placa ?? (placaKey ? placaKey : 'SEM PLACA DEFINIDA'),
          modelo: res?.modelo ?? '',
          ano_modelo: Number(res?.ano_modelo) || 0,
          categoria: res?.categoria ?? '',
          associado: res?.associado ?? '',
          cpf_associado: res?.cpf_associado ?? '',
          tempo_participacao_meses: Number(res?.tempo_participacao_meses) || 0,
          regional: res?.regional ?? '',
          consultor: res?.consultor ?? '',
          cidade: res?.cidade ?? '',
          situacao: res?.situacao ?? (res?.is_ativa === true ? 'ATIVA' : (res?.is_ativa === false ? 'INATIVA' : '')),
          resultado: resultadoBack,
          cadastroSga: res?.cadastroSga !== false,
          semPlacaInformada: !placaKey,
          detalhamento: {
            receitaMensalidade,
            receitaNasajonComPlaca,
            receitaRateio,
            despesaNasajonComPlaca,
            despesaEasy,
            despesaRateio,
            despesaEasyExcedente,
            despesaRecorrente,
            despesaRecorrenteExcedente,
            totaisEntrada,
            totaisSaida,
            resultado: resultadoBack,
          },
          entradasClassificacoes: entradasOp,
          saidasClassificacoes: saidasOp,
          entradasClassificacoesRateio: entradasRateio,
          saidasClassificacoesRateio: saidasRateio,
        };

        setResultado(adapted);
        setExpanded([]);
      } catch {
        if (!active) return;
        toast.error('Erro ao buscar DRE da placa.');
        setResultado(null);
      } finally {
        if (!active) return;
        setLoading(false);
      }
    };

    run();

    return () => {
      active = false;
    };
  }, [placa, dataInicio, dataFim, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const r = resultado;

  const receitaBruta = Number(r?.detalhamento?.totaisEntrada) || 0;
  const receitaOperacional =
    (Number(r?.detalhamento?.receitaMensalidade) || 0) + (Number(r?.detalhamento?.receitaNasajonComPlaca) || 0);
  const outrasReceitas = Number(r?.detalhamento?.receitaRateio) || 0;

  const despesasOperacionais =
    (Number(r?.detalhamento?.despesaNasajonComPlaca) || 0) +
    (Number(r?.detalhamento?.despesaEasy) || 0) +
    (Number(r?.detalhamento?.despesaRecorrente) || 0);

  const margemContribuicao = Number(r?.detalhamento?.despesaRateio) || 0;

  const resultadoOperacional = receitaBruta - despesasOperacionais;
  const resultadoFinal = Number(r?.detalhamento?.resultado) || 0;
  const baseRbAbs = Math.abs(receitaBruta ?? 0);

  return (
    <>
      {downloading && <FullScreenLoader />}

      <div className="status-modal-overlay">
        <div className="status-modal-dre-content" ref={modalRef}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div className="modal-header">{placa || 'SEM PLACA DEFINIDA'}</div>

            <div
              className="dre-save-btn MouseHover"
              onClick={handleDownloadCsv}
              aria-disabled={downloading}
              style={{ opacity: downloading ? 0.6 : 1, pointerEvents: downloading ? 'none' : 'auto' }}
              title="Gerar CSV do DRE da placa"
            >
              <div className="Frame1000003987">
                <img
                  src={ContacDocumentOrange}
                  alt="Relatórios"
                  width="19.80"
                  height="19.80"
                  style={{ cursor: 'pointer' }}
                />
                <div className="ComponentName">{downloading ? 'Gerando...' : 'Gerar CSV'}</div>
              </div>
            </div>

            <div
              className="dre-save-btn MouseHover"
              onClick={handleDownloadXlsx}
              aria-disabled={downloading}
              style={{ opacity: downloading ? 0.6 : 1, pointerEvents: downloading ? 'none' : 'auto' }}
              title="Gerar XLSX do DRE da placa"
            >
              <div className="Frame1000003987">
                <img
                  src={ContacDocumentOrange}
                  alt="Relatórios"
                  width="19.80"
                  height="19.80"
                  style={{ cursor: 'pointer' }}
                />
                <div className="ComponentName">{downloading ? 'Gerando...' : 'Gerar XLSX'}</div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="modal-body" style={{ flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '60px 0', gap: '10px' }}>
              <div className="dre-spinner" />
              <span style={{ fontSize: '14px', color: 'var(--tema-texto-2, #666)', fontWeight: '500', fontFamily: "'AmpleSoft-Regular', sans-serif" }}>Carregando...</span>
            </div>
          ) : !r ? (
            <div className="modal-body">
              <div className="card"><div className="card-entry">Nenhum dado encontrado para esta placa nesse período.</div></div>
            </div>
          ) : (
            <>
              {/* O usuário procura a placa no SGA achando que falta preencher o cadastro; a faixa
                  explica que não há cadastro a consultar — e que na maioria das vezes isso é esperado */}
              {r.semPlacaInformada ? (
                <div className="dre-aviso-sem-cadastro">
                  Estes são os lançamentos que chegaram do financeiro <strong>sem placa informada</strong>. Por isso não
                  há veículo, associado nem consultor para exibir.
                </div>
              ) : !r.cadastroSga ? (
                <div className="dre-aviso-sem-cadastro">
                  Esta placa <strong>não está no cadastro de veículos do SGA</strong>, por isso os dados do veículo
                  aparecem vazios abaixo. Isso é <strong>esperado em veículos de terceiros</strong>, que não são
                  cadastrados no SGA mas geram movimento financeiro vindo de outras fontes (Nasajon, Easy Assist e
                  demais origens). Se esta placa deveria ser de um associado, vale conferir como ela foi registrada na
                  origem.
                </div>
              ) : null}

            <div className="modal-body">
              {/* Card 1 - Suas Informações (Dados do Veículo) */}
              <div className="card card-left">
                <div className="modal-header">Dados do Veículo</div>
                {[
                  ['Associado', r.associado],
                  ['CPF', r.cpf_associado],
                  ['Placa', r.placa],
                  ['Veículo', r.modelo],
                  ['Ano Mod', r.ano_modelo],
                  ['Categoria', r.categoria],
                  ['Tempo de Associação', `${r.tempo_participacao_meses} meses`],
                  ['Regional', r.regional],
                  ['Consultor', r.consultor],
                  ['Cidade', r.cidade],
                  ['Situação', r.situacao],
                ].map(([label, valor], idx) => (
                  <div className="info-row" key={idx}>
                    <span className="info-label">{label}</span>
                    <span className="info-value">{String(valor ?? '')}</span>
                  </div>
                ))}
              </div>

              {/* Card 2 - DRE */}
              <div className="card">
                <div className="modal-header">Demonstrativo de Resultados</div>
                <div className="row-between dre-columns-header">
                  <div />
                  <div className="dre-value-pct">
                    <span className="dre-col-header" style={{ minWidth: 120, textAlign: 'right' }}>Valor</span>
                    <span className="dre-col-header" style={{ minWidth: 70, textAlign: 'right' }}>% Rec. Bruta</span>
                  </div>
                </div>

                <div className="row-between">
                  <div className="dre-title">
                    Receita Bruta <span className="dre-note">(Todas as Receitas)</span>
                  </div>
                  {renderValorPct(
                    <span className="entry-text value-positive3">R$ {formatCurrency(receitaBruta)}</span>,
                    +Math.abs(receitaBruta),
                    baseRbAbs,
                    'value-positive5'
                  )}
                </div>

                <div
                  className="row-between clickable"
                  onClick={() => toggleExpand('op')}
                  aria-expanded={expanded.includes('op')}
                  title="Ver detalhes da Receita Operacional"
                >
                  <div className="dre-title">
                    Receita Operacional <span className="dre-note">(Receitas ligadas às placas)</span>
                    <img
                      style={{ marginLeft: 5 }}
                      src={expanded.includes('op') ? ArrowUp : ArrowDown}
                      alt="Toggle"
                      className="imgIcon arrow-toggle"
                    />
                  </div>
                  {renderValorPct(
                    <span className="entry-text value-positive3">R$ {formatCurrency(receitaOperacional)}</span>,
                    +Math.abs(receitaOperacional),
                    baseRbAbs,
                    'value-positive5'
                  )}
                </div>

                {expanded.includes('op') && (
                  <div className="subclass-container">
                    {(r.entradasClassificacoes || []).map((ent, idx) => (
                      <React.Fragment key={`ent-op-${idx}`}>
                        <div className="subclass-row">
                          <div className="subclass-label">
                            <span>{ent.classificacao}</span>
                            <img
                              src={expanded.includes(`ent-op-${idx}`) ? ArrowUp : ArrowDown}
                              alt="Toggle"
                              className="imgIcon arrow-toggle clickable"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(`ent-op-${idx}`);
                              }}
                            />
                          </div>
                          <div className="subclass-value-pct">
                            <span className="subclass-value value-positive4">R$ {formatCurrency(ent.total)}</span>
                            <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                          </div>
                        </div>

                        {expanded.includes(`ent-op-${idx}`) &&
                          (ent.detalhes || []).map((det, dIdx) => (
                            <div className="detail-row" key={`ent-op-det-${idx}-${dIdx}`}>
                              <span className="detail-label">{det.mes}</span>
                              <div className="subclass-value-pct">
                                <span className="detail-value value-positive4">R$ {formatCurrency(det.valor)}</span>
                                <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                              </div>
                            </div>
                          ))}
                      </React.Fragment>
                    ))}
                  </div>
                )}

                <div className="row-between">
                  <div className="dre-title">
                    Outras Receitas <span className="dre-note">(Receitas não ligadas às placas)</span>
                  </div>
                  {renderValorPct(
                    <span className="entry-text value-positive3">R$ {formatCurrency(outrasReceitas)}</span>,
                    +Math.abs(outrasReceitas),
                    baseRbAbs,
                    'value-positive5'
                  )}
                </div>

                <div
                  className="row-between clickable"
                  onClick={() => toggleExpand('co')}
                  aria-expanded={expanded.includes('co')}
                  title="Ver detalhes de Despesas Operacionais"
                >
                  <div className="dre-title">
                    Despesas Operacionais <span className="dre-note">(Despesas ligadas às placas)</span>
                    <img
                      style={{ marginLeft: 5 }}
                      src={expanded.includes('co') ? ArrowUp : ArrowDown}
                      alt="Toggle"
                      className="imgIcon arrow-toggle"
                    />
                  </div>
                  {renderValorPct(
                    <span className="entry-text value-negative3">R$ -{formatCurrency(despesasOperacionais)}</span>,
                    -Math.abs(despesasOperacionais),
                    baseRbAbs,
                    'value-negative5'
                  )}
                </div>

                {expanded.includes('co') && (
                  <div className="subclass-container">
                    {(r.saidasClassificacoes || [])
                      .filter((x) => (Number(x.total) || 0) !== 0)
                      .map((sai, idx) => (
                        <React.Fragment key={`sai-dir-${idx}`}>
                          <div className="subclass-row">
                            <div className="subclass-label">
                              <span>{sai.classificacao}</span>
                              <img
                                src={expanded.includes(`sai-dir-${idx}`) ? ArrowUp : ArrowDown}
                                alt="Toggle"
                                className="imgIcon arrow-toggle clickable"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleExpand(`sai-dir-${idx}`);
                                }}
                              />
                            </div>
                            <div className="subclass-value-pct">
                              <span className="subclass-value value-negative4">R$ {formatCurrency(sai.total)}</span>
                              <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                            </div>
                          </div>

                          {expanded.includes(`sai-dir-${idx}`) &&
                            (sai.detalhes || []).map((det, dIdx) => (
                              <div className="detail-row" key={`sai-dir-det-${idx}-${dIdx}`}>
                                <span className="detail-label">{det.mes}</span>
                                <div className="subclass-value-pct">
                                  <span className="detail-value value-negative4">R$ {formatCurrency(det.valor)}</span>
                                  <span className="dre-percent" style={{ visibility: 'hidden' }}>—</span>
                                </div>
                              </div>
                            ))}
                        </React.Fragment>
                      ))}
                  </div>
                )}

                <div className="row-between">
                  <div className="dre-title">
                    Resultado Operacional <span className="dre-note">(Receita Bruta - Desp. Operacional)</span>
                  </div>
                  {renderValorPct(
                    <span className={`entry-text ${classBySign3(resultadoOperacional)}`}>
                      R$ {formatCurrency(resultadoOperacional)}
                    </span>,
                    Number(resultadoOperacional) || 0,
                    baseRbAbs,
                    (Number(resultadoOperacional) || 0) < 0 ? 'value-negative5' : 'value-positive5'
                  )}
                </div>

                <div className="row-between">
                  <div className="dre-title">
                    Margem de Contribuição <span className="dre-note">(Rateio de despesas não ligadas às placas)</span>
                  </div>
                  {renderValorPct(
                    <span className="entry-text value-negative3">R$ -{formatCurrency(margemContribuicao)}</span>,
                    -Math.abs(margemContribuicao),
                    baseRbAbs,
                    'value-negative5'
                  )}
                </div>

                <div className="row-between">
                  <div className="dre-title">
                    Resultado <span className="dre-note">(Resultado Operacional - Margem de Contribuição)</span>
                  </div>
                  {renderValorPct(
                    <span className={`entry-text ${classBySign3(resultadoFinal)}`}>R$ {formatCurrency(resultadoFinal)}</span>,
                    Number(resultadoFinal) || 0,
                    baseRbAbs,
                    (Number(resultadoFinal) || 0) < 0 ? 'value-negative5' : 'value-positive5'
                  )}
                </div>

                {/* "Por que este valor": os eventos do SGA no mesmo período da DRE. Fica DEPOIS
                    do demonstrativo de propósito — primeiro o número, depois a explicação. */}
                {mostrarEventos && !r.semPlacaInformada && <BlocoEventosDaPlaca placa={r.placa} />}

                <div className="modal-footer">
                  <button className="modal-close-btn" onClick={onClose}>Fechar</button>
                </div>
              </div>
            </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ModalPlacaDre;

const mapClassifs = (arr: any[] = []): ClassificacaoResp[] =>
  arr.map((c: any) => ({
    classificacao: c?.classificacao ?? 'Sem Classificação',
    total: toNum(c?.total),
    detalhes: (Array.isArray(c?.porMes) ? c.porMes : c?.detalhes || []).map((m: any) => ({
      mes: m?.competencia ?? m?.mes ?? '',
      valor: toNum(m?.valor),
    })),
  }));
